import { useCallback, useEffect, useRef, useState } from "react";
import {
  applyHighlightStylePatch,
  areHighlightListsEqual,
  canHighlightTextSource,
  findHighlightAtOffset,
  findHighlightByPhrase,
  findHighlightContainingRange,
  highlightSourceText,
  isHighlightPhraseInText,
  expandCompactPhraseInText,
  isHighlightStyleField,
  normalizeHighlightPhrase,
  pruneInactiveHighlights,
} from "../utils/textHighlights";

export const HIGHLIGHT_FIELD_UNCHANGED = Symbol("highlight-field-unchanged");

export const applyIfHighlightField = (result, commitStyle) => {
  if (result == null) return false;
  if (result !== HIGHLIGHT_FIELD_UNCHANGED) commitStyle(result);
  return true;
};

export const defaultCanHighlight = (node) =>
  node?.type === "text" && canHighlightTextSource(node?.source?.kind);

/**
 * Inspector values a highlight overrides, laid over the layer's own style.
 * Stroke/background color stay visible with empty px while that phrase is
 * selected; leftover paint is pruned when the highlight selection goes away.
 */
export const overlayHighlightInspector = ({ node, phrase }) => {
  const slot = findHighlightByPhrase({
    highlights: node?.style?.highlights,
    phrase,
  });
  if (!slot) return {};
  const overlay = {};
  if (String(slot.color || "").trim()) overlay.color = slot.color;
  if (String(slot.strokeColor || "").trim()) {
    overlay.strokeColor = slot.strokeColor;
    overlay.strokeWidth =
      Number(slot.strokeWidth) > 0 ? slot.strokeWidth : "";
  }
  if (String(slot.backgroundColor || "").trim()) {
    overlay.backgroundColor = slot.backgroundColor;
    overlay.backgroundPadding =
      Number(slot.backgroundPadding) > 0 ? slot.backgroundPadding : "";
    const radius = Number(slot.backgroundRadius);
    if (
      slot.backgroundRadius != null &&
      slot.backgroundRadius !== "" &&
      Number.isFinite(radius) &&
      radius >= 0
    ) {
      overlay.backgroundRadius = radius;
    }
  }
  if (String(slot.fontAssetRef || "").trim()) {
    overlay.fontAssetRef = slot.fontAssetRef;
  }
  if (String(slot.fontFaceRef || "").trim()) {
    overlay.fontFaceRef = slot.fontFaceRef;
  }
  if (Number(slot.fontWeight) > 0) overlay.fontWeight = slot.fontWeight;
  if (String(slot.textStyle || "").trim()) overlay.textStyle = slot.textStyle;
  return overlay;
};

/**
 * Selecting part of a text layer on the canvas and styling only that phrase.
 * Shared by the template editor and the instance/image edit modals, which
 * differ in how they store layers but not in how the selection behaves.
 *
 * `getNode` resolves a layer id to its node, and `getNodeText(node, nodeID)`
 * reads the text a phrase is matched against — the instance modals resolve it
 * per item, not from the node. Both are held in refs, so callers can pass
 * inline closures without making the returned callbacks unstable.
 */
const useTextHighlightSelection = ({
  selectedID,
  editingTextID,
  canvasSelector,
  getNode,
  getNodeText = highlightSourceText,
  // Whether a layer accepts highlights at all. Template nodes gate on their
  // source kind; image layers have no source and gate on their type alone.
  canHighlight = defaultCanHighlight,
  // Anything that changes when the layers change (the nodes map, the layer
  // list). Re-checks that the selected phrase still exists in the text.
  nodesVersion,
  persistHighlights,
} = {}) => {
  const [textSelection, setTextSelection] = useState(null);
  const textSelectionRef = useRef(null);
  const previousSelectionRef = useRef(null);
  const keepTextEditingRef = useRef(false);
  const getNodeRef = useRef(getNode);
  const getNodeTextRef = useRef(getNodeText);
  const canHighlightRef = useRef(canHighlight);
  const persistHighlightsRef = useRef(persistHighlights);
  getNodeRef.current = getNode;
  getNodeTextRef.current = getNodeText;
  canHighlightRef.current = canHighlight;
  persistHighlightsRef.current = persistHighlights;

  useEffect(() => {
    textSelectionRef.current = textSelection;
  }, [textSelection]);

  useEffect(() => {
    const previous = previousSelectionRef.current;
    previousSelectionRef.current = textSelection;
    const previousID = previous?.nodeID;
    if (!previousID) return;
    const stillOnSamePhrase =
      textSelection?.nodeID === previous.nodeID &&
      textSelection?.phrase === previous.phrase;
    if (stillOnSamePhrase) return;
    const node = getNodeRef.current?.(previousID);
    const current = node?.style?.highlights;
    const next = pruneInactiveHighlights(current);
    if (areHighlightListsEqual(current, next)) return;
    persistHighlightsRef.current?.({ nodeID: previousID, highlights: next });
  }, [textSelection]);

  useEffect(() => {
    if (!editingTextID || !canvasSelector) {
      keepTextEditingRef.current = false;
      return undefined;
    }
    // Track every pointerdown, not just the ones outside the canvas: a flag
    // that is only ever set to true survives past the blur it was meant for
    // and keeps the next text edit open for no reason.
    const onPointerDown = (event) => {
      keepTextEditingRef.current = !event.target?.closest?.(canvasSelector);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [canvasSelector, editingTextID]);

  useEffect(() => {
    if (!textSelection) return;
    if (textSelection.nodeID !== selectedID) setTextSelection(null);
  }, [selectedID, textSelection]);

  useEffect(() => {
    if (!textSelection) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setTextSelection(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [textSelection]);

  // Drop a selection whose phrase no longer exists, e.g. after the text was
  // edited or the layer stopped being a highlightable source. The node is read
  // inside the effect: resolvers usually rebuild it on every render, so having
  // it as a dependency would re-run this constantly.
  useEffect(() => {
    if (!textSelection) return;
    const node = getNodeRef.current?.(selectedID);
    if (!canHighlightRef.current(node)) {
      setTextSelection(null);
      return;
    }
    const origin = getNodeTextRef.current(node, selectedID);
    if (
      !isHighlightPhraseInText({ text: origin, phrase: textSelection.phrase })
    ) {
      setTextSelection(null);
    }
  }, [nodesVersion, selectedID, textSelection]);

  const onTextSelectionChange = useCallback((nodeID, state) => {
    // No node means the click landed outside any layer: always clear.
    // Otherwise clear only when the standing selection belongs to that layer.
    const clear = () =>
      setTextSelection((current) =>
        !current || !nodeID || current.nodeID === nodeID ? null : current
      );
    if (!nodeID || !state) {
      clear();
      return;
    }
    const apply = (phrase, kind) =>
      setTextSelection((current) =>
        current?.nodeID === nodeID &&
        current.phrase === phrase &&
        current.kind === kind
          ? current
          : { nodeID, phrase, kind }
      );
    if (state.kind !== "range" && state.kind !== "caret") {
      clear();
      return;
    }
    // Existing highlights are resolved here rather than in the editor, which
    // is frozen against re-renders and cannot see the current node.
    const node = getNodeRef.current?.(nodeID);
    const origin = String(state.text || "") || getNodeTextRef.current(node, nodeID);
    const highlights = node?.style?.highlights;

    if (state.kind === "caret") {
      const phrase = normalizeHighlightPhrase(
        findHighlightAtOffset({ text: origin, highlights, offset: state.offset })
          ?.example
      );
      if (!phrase) clear();
      else apply(phrase, "caret");
      return;
    }

    let phrase = normalizeHighlightPhrase(state.phrase);
    if (!isHighlightPhraseInText({ text: origin, phrase })) {
      phrase = expandCompactPhraseInText({ text: origin, phrase: state.phrase });
    }
    if (!phrase) {
      clear();
      return;
    }
    // Selecting part of a styled phrase targets the whole highlight, so the
    // panel reflects it and the edit lands on it instead of spawning a slot
    // for the fragment. Offsets are absent in the read-only paint, and then
    // only an exact phrase match can hit.
    const containing = findHighlightContainingRange({
      text: origin,
      highlights,
      start: state.start,
      end: state.end,
    });
    apply(normalizeHighlightPhrase(containing?.example) || phrase, "range");
  }, []);

  /**
   * The style object this field change should produce when it belongs to the
   * selected phrase, or null when the caller should patch the layer as usual.
   */
  const styleForHighlightField = useCallback(({ node, nodeID, key, value }) => {
    const phrase = String(textSelectionRef.current?.phrase || "").trim();
    if (
      !phrase ||
      !isHighlightStyleField(key) ||
      !canHighlightRef.current(node) ||
      textSelectionRef.current?.nodeID !== nodeID ||
      !isHighlightPhraseInText({
        text: getNodeTextRef.current(node, nodeID),
        phrase,
      })
    ) {
      return null;
    }
    const nextHighlights = applyHighlightStylePatch({
      highlights: node.style?.highlights,
      phrase,
      key,
      value,
      text: getNodeTextRef.current(node, nodeID),
    });
    if (areHighlightListsEqual(node.style?.highlights, nextHighlights)) {
      return HIGHLIGHT_FIELD_UNCHANGED;
    }
    const style = { ...(node.style || {}) };
    if (nextHighlights.length) style.highlights = nextHighlights;
    else delete style.highlights;
    return style;
  }, []);

  const phrase =
    textSelection?.nodeID === selectedID
      ? String(textSelection.phrase || "").trim()
      : "";

  // Stable while the phrase holds, so consumers can list it as an effect
  // dependency without re-running the form reset on every render.
  const inspectorOverlay = useCallback(
    (node) => (phrase ? overlayHighlightInspector({ node, phrase }) : {}),
    [phrase]
  );

  return {
    textSelection,
    textSelectionRef,
    keepTextEditingRef,
    setTextSelection,
    onTextSelectionChange,
    styleForHighlightField,
    selectedPhrase: phrase,
    isTextRangeSelected: Boolean(phrase) && textSelection?.kind === "range",
    inspectorOverlay,
  };
};

export default useTextHighlightSelection;
